import { c as _c } from "react/compiler-runtime";
import { useState, useCallback, useEffect, useRef } from "react";
import { useLocalStorage } from "usehooks-ts";
export const MIN_SIDEBAR_WIDTH = 240;
export const MAX_SIDEBAR_WIDTH = 480;

/**
 * Key/default are exported so the loading skeleton (SidebarSkeleton) can
 * render at the width the real sidebar will restore.
 */
export const SESSION_SIDEBAR_WIDTH_KEY = "assistantSidebarWidth";
export const DEFAULT_SESSION_SIDEBAR_WIDTH = 330;
export function clampSidebarWidth(width: number): number {
  return Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, width));
}
export function useResizableSidebar() {
  const $ = _c(10);
  const [storedWidth, setSidebarWidth] = useLocalStorage(SESSION_SIDEBAR_WIDTH_KEY, DEFAULT_SESSION_SIDEBAR_WIDTH);
  let t0;
  if ($[0] !== storedWidth) {
    t0 = clampSidebarWidth(storedWidth);
    $[0] = storedWidth;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const sidebarWidth = t0;
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef(null);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = e => {
      if (e.button !== 0) {
        return;
      }
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      setIsResizing(true);
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleResizePointerDown = t1;
  let t2;
  let t3;
  if ($[3] !== isResizing || $[4] !== setSidebarWidth) {
    t2 = () => {
      if (!isResizing) {
        return;
      }
      let rafId = null;
      let pendingWidth = null;
      const handlePointerUp = () => {
        if (pendingWidth !== null) {
          setSidebarWidth(pendingWidth);
        }
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
        setIsResizing(false);
      };
      const handlePointerMove = e_0 => {
        if ((e_0.buttons & 1) === 0) {
          handlePointerUp();
          return;
        }
        if (!sidebarRef.current) {
          return;
        }
        const containerRect = sidebarRef.current.getBoundingClientRect();
        const newWidth = e_0.clientX - containerRect.left;
        pendingWidth = clampSidebarWidth(newWidth);
        if (rafId === null) {
          rafId = requestAnimationFrame(() => {
            if (pendingWidth !== null) {
              setSidebarWidth(pendingWidth);
            }
            rafId = null;
          });
        }
      };
      document.addEventListener("pointermove", handlePointerMove);
      document.addEventListener("pointerup", handlePointerUp);
      document.addEventListener("pointercancel", handlePointerUp);
      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";
      return () => {
        document.removeEventListener("pointermove", handlePointerMove);
        document.removeEventListener("pointerup", handlePointerUp);
        document.removeEventListener("pointercancel", handlePointerUp);
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
      };
    };
    t3 = [isResizing, setSidebarWidth];
    $[3] = isResizing;
    $[4] = setSidebarWidth;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  let t4;
  if ($[7] !== isResizing || $[8] !== sidebarWidth) {
    t4 = {
      sidebarWidth,
      isResizing,
      sidebarRef,
      handleResizePointerDown
    };
    $[7] = isResizing;
    $[8] = sidebarWidth;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}